/**
 * Taalbegrip (TAA) — 35 families, 4 skills
 *
 * Skills: zinsontleding (10), woordsoorten (8), tekstbegrip (13), woordenschat (4)
 *
 * Split from taxonomyRegistry.ts in R23.
 *
 * LDG Audit (16 mrt 2026): 4 tier-aanpassingen doorgevoerd:
 * - TAA-LEZEN-DETAIL: E6 (tier 5) → B6+ (tier 2) — basaalste leesvaardigheid, Bloom niveau 1
 * - TAA-LEZEN-FEIT: M8 (tier 14) → E7 (tier 11) — standaard 1F-stof, SLO kerndoel 9
 * - TAA-LEZEN-VERB: E8+ (tier 16) → B8 (tier 13) — CITO-kritiek, meest voorkomend vraagtype eindtoets
 * - TAA-WOORD-SYNONIEM: B7 (tier 7) → M6 (tier 3) — woordenschat begint groep 6
 */

import type { FamilyDefinition } from '../types'

export const TAA_FAMILIES: FamilyDefinition[] = [
  // 6.1 Skill: zinsontleding (10 families)
  {
    familyId: 'TAA-ONT-BASIS',
    module: 'Taalbegrip',
    skill: 'zinsontleding',
    subSkill: 'ontl_pv_onderwerp',
    tier: 2,
    levelCode: 'B6+',
    description: 'Persoonsvorm en onderwerp',
  },
  {
    familyId: 'TAA-ONT-LV-MV',
    module: 'Taalbegrip',
    skill: 'zinsontleding',
    subSkill: 'ontl_lv_mv',
    tier: 9,
    levelCode: 'M7',
    description: 'Lijdend en meewerkend voorwerp',
  },
  {
    familyId: 'TAA-ONT-BWB',
    module: 'Taalbegrip',
    skill: 'zinsontleding',
    subSkill: 'ontl_bwb',
    tier: 13,
    levelCode: 'B8',
    description: 'Bijwoordelijke bepalingen',
  },
  {
    familyId: 'TAA-ONT-GEZEGDE',
    module: 'Taalbegrip',
    skill: 'zinsontleding',
    subSkill: 'ontl_gezegde',
    tier: 5,
    levelCode: 'E6',
    description: 'Gezegde herkennen',
  },
  {
    familyId: 'TAA-ONT-NAAMWOORDELIJK',
    module: 'Taalbegrip',
    skill: 'zinsontleding',
    subSkill: 'ontl_naamwoordelijk',
    tier: 11,
    levelCode: 'E7',
    description: 'Naamwoordelijk deel',
  },
  {
    familyId: 'TAA-ONT-VOORZETSEL',
    module: 'Taalbegrip',
    skill: 'zinsontleding',
    subSkill: 'ontl_voorzetsel',
    tier: 7,
    levelCode: 'B7',
    description: 'Voorzetselvoorwerp',
  },
  {
    familyId: 'TAA-ONT-BIJZIN',
    module: 'Taalbegrip',
    skill: 'zinsontleding',
    subSkill: 'ontl_bijzin',
    tier: 13,
    levelCode: 'B8',
    description: 'Bijzinnen herkennen',
  },
  {
    familyId: 'TAA-ONT-HOOFDZIN',
    module: 'Taalbegrip',
    skill: 'zinsontleding',
    subSkill: 'ontl_hoofdzin',
    tier: 11,
    levelCode: 'E7',
    description: 'Hoofdzinnen en nevenschikking',
  },
  {
    familyId: 'TAA-ONT-BETREKKELIJK',
    module: 'Taalbegrip',
    skill: 'zinsontleding',
    subSkill: 'ontl_betrekkelijk',
    tier: 15,
    levelCode: 'E8',
    description: 'Betrekkelijke bijzinnen',
  },
  {
    familyId: 'TAA-ONT-INVERSIE',
    module: 'Taalbegrip',
    skill: 'zinsontleding',
    subSkill: 'ontl_inversie',
    tier: 9,
    levelCode: 'M7',
    description: 'Inversie in zinnen',
  },
  // 6.2 Skill: woordsoorten (8 families)
  {
    familyId: 'TAA-SOORT-LID',
    module: 'Taalbegrip',
    skill: 'woordsoorten',
    subSkill: 'srt_lid_znw',
    tier: 3,
    levelCode: 'M6',
    description: 'Lidwoord en zelfstandig naamwoord',
  },
  {
    familyId: 'TAA-SOORT-VNW',
    module: 'Taalbegrip',
    skill: 'woordsoorten',
    subSkill: 'srt_voornaamwoorden',
    tier: 10,
    levelCode: 'M7+',
    description: 'Voornaamwoorden',
  },
  {
    familyId: 'TAA-SOORT-BNW',
    module: 'Taalbegrip',
    skill: 'woordsoorten',
    subSkill: 'srt_bijvoeglijk_nw',
    tier: 5,
    levelCode: 'E6',
    description: 'Bijvoeglijk naamwoord',
  },
  {
    familyId: 'TAA-SOORT-WW',
    module: 'Taalbegrip',
    skill: 'woordsoorten',
    subSkill: 'srt_werkwoord',
    tier: 4,
    levelCode: 'M6+',
    description: 'Werkwoord',
  },
  {
    familyId: 'TAA-SOORT-BW',
    module: 'Taalbegrip',
    skill: 'woordsoorten',
    subSkill: 'srt_bijwoord',
    tier: 7,
    levelCode: 'B7',
    description: 'Bijwoord',
  },
  {
    familyId: 'TAA-SOORT-VZ',
    module: 'Taalbegrip',
    skill: 'woordsoorten',
    subSkill: 'srt_voorzetsel',
    tier: 9,
    levelCode: 'M7',
    description: 'Voorzetsel',
  },
  {
    familyId: 'TAA-SOORT-VW',
    module: 'Taalbegrip',
    skill: 'woordsoorten',
    subSkill: 'srt_voegwoord',
    tier: 11,
    levelCode: 'E7',
    description: 'Voegwoord',
  },
  {
    familyId: 'TAA-SOORT-TW',
    module: 'Taalbegrip',
    skill: 'woordsoorten',
    subSkill: 'srt_telwoord',
    tier: 7,
    levelCode: 'B7',
    description: 'Telwoord',
  },
  // 6.3 Skill: tekstbegrip (13 families)
  {
    familyId: 'TAA-LEZEN-VERW',
    module: 'Taalbegrip',
    skill: 'tekstbegrip',
    subSkill: 'tekst_verwijswoorden',
    tier: 7,
    levelCode: 'B7',
    description: 'Verwijswoorden',
  },
  {
    familyId: 'TAA-LEZEN-KERN',
    module: 'Taalbegrip',
    skill: 'tekstbegrip',
    subSkill: 'tekst_hoofdgedachte',
    tier: 9,
    levelCode: 'M7',
    description: 'Hoofdgedachte',
  },
  {
    familyId: 'TAA-LEZEN-FEIT',
    module: 'Taalbegrip',
    skill: 'tekstbegrip',
    subSkill: 'tekst_feit_mening',
    tier: 11,
    levelCode: 'E7',
    description: 'Feit en mening onderscheiden (1F kerndoel)',
  },
  {
    familyId: 'TAA-LEZEN-VERB',
    module: 'Taalbegrip',
    skill: 'tekstbegrip',
    subSkill: 'tekst_verbanden',
    tier: 13,
    levelCode: 'B8',
    description: 'Tekstverbanden herkennen (CITO essentieel)',
  },
  {
    familyId: 'TAA-SIGNAALWOORD',
    module: 'Taalbegrip',
    skill: 'tekstbegrip',
    subSkill: 'tekst_signaalwoorden',
    tier: 10,
    levelCode: 'M7+',
    description: 'Signaalwoorden herkennen (CITO essentieel)',
  },
  {
    familyId: 'TAA-TEKSTSOORT',
    module: 'Taalbegrip',
    skill: 'tekstbegrip',
    subSkill: 'tekst_tekstsoorten',
    tier: 7,
    levelCode: 'B7',
    description: 'Tekstsoorten onderscheiden (kerndoel)',
  },
  {
    familyId: 'TAA-LEZEN-DETAIL',
    module: 'Taalbegrip',
    skill: 'tekstbegrip',
    subSkill: 'tekst_detail',
    tier: 2,
    levelCode: 'B6+',
    description: 'Detailinformatie vinden (basale leesvaardigheid)',
  },
  {
    familyId: 'TAA-LEZEN-CONCLUSIE',
    module: 'Taalbegrip',
    skill: 'tekstbegrip',
    subSkill: 'tekst_conclusie',
    tier: 13,
    levelCode: 'B8',
    description: 'Conclusies trekken',
  },
  {
    familyId: 'TAA-LEZEN-VOORSPEL',
    module: 'Taalbegrip',
    skill: 'tekstbegrip',
    subSkill: 'tekst_voorspellen',
    tier: 11,
    levelCode: 'E7',
    description: 'Voorspellen en verwachten',
  },
  {
    familyId: 'TAA-LEZEN-SAMENVATTEN',
    module: 'Taalbegrip',
    skill: 'tekstbegrip',
    subSkill: 'tekst_samenvatten',
    tier: 11,
    levelCode: 'E7',
    description: 'Samenvatten',
  },
  {
    familyId: 'TAA-LEZEN-STRUCTUUR',
    module: 'Taalbegrip',
    skill: 'tekstbegrip',
    subSkill: 'tekst_structuur',
    tier: 13,
    levelCode: 'B8',
    description: 'Tekststructuur herkennen',
  },
  {
    familyId: 'TAA-LEZEN-DOEL',
    module: 'Taalbegrip',
    skill: 'tekstbegrip',
    subSkill: 'tekst_doel_auteur',
    tier: 15,
    levelCode: 'E8',
    description: 'Doel auteur bepalen',
  },
  {
    familyId: 'TAA-ARGUMENTATIE',
    module: 'Taalbegrip',
    skill: 'tekstbegrip',
    subSkill: 'tekst_argumentatie',
    tier: 14,
    levelCode: 'M8',
    description: 'Argumentatiestructuur (kerndoel)',
  },
  // 6.4 Skill: woordenschat (4 families)
  {
    familyId: 'TAA-WOORD-SYNONIEM',
    module: 'Taalbegrip',
    skill: 'woordenschat',
    subSkill: 'woord_synoniem',
    tier: 3,
    levelCode: 'M6',
    description: 'Synoniemen (basiswoordenschat groep 6)',
  },
  {
    familyId: 'TAA-WOORD-ANTONIEM',
    module: 'Taalbegrip',
    skill: 'woordenschat',
    subSkill: 'woord_antoniem',
    tier: 8,
    levelCode: 'B7+',
    description: 'Antoniemen',
  },
  {
    familyId: 'TAA-WOORD-CONTEXT',
    module: 'Taalbegrip',
    skill: 'woordenschat',
    subSkill: 'woord_betekenis_context',
    tier: 9,
    levelCode: 'M7',
    description: 'Betekenis uit context',
  },
  {
    familyId: 'TAA-WOORD-IDIOOM',
    module: 'Taalbegrip',
    skill: 'woordenschat',
    subSkill: 'woord_idioom',
    tier: 13,
    levelCode: 'B8',
    description: 'Uitdrukkingen en gezegden',
  },
]
